import type { MedplumClient } from '@medplum/core';
import type { Appointment, Patient } from '@medplum/fhirtypes';
import { pickSessionEncounter } from '@unify/fhir-model/resources/client-location-confirmation';
import { fetchClientSessionLocationStatus } from '@/lib/telehealth/chime/chimeTelehealthService';
import {
  getClientLocationConfirmationForPatient,
  getClientLocationConfirmations,
  patientHomeAddressPrefill,
} from './clientLocationConfirmationFhir';
import type { ClientLocationConfirmation } from './types';

export { patientHomeAddressPrefill };

export async function appointmentRequiresLocationConfirmation(
  medplum: MedplumClient,
  appointmentId: string,
  patientId?: string,
): Promise<{ required: boolean; appointment: Appointment; existing: ClientLocationConfirmation | null }> {
  // A Patient cannot read Encounter.location (hidden from clients); the bot answers for their own.
  if (patientId && medplum.getProfile()?.resourceType === 'Patient') {
    const [appointment, confirmed] = await Promise.all([
      medplum.readResource('Appointment', appointmentId),
      fetchClientSessionLocationStatus(medplum, appointmentId),
    ]);
    return { required: !confirmed, appointment, existing: null };
  }
  const [appointment, encounters] = await Promise.all([
    medplum.readResource('Appointment', appointmentId),
    medplum.searchResources('Encounter', { appointment: `Appointment/${appointmentId}`, _count: '20' }),
  ]);
  // The same Encounter the Chime bots write to (`pickSessionEncounter`), so the UI and the bots agree.
  const encounter = pickSessionEncounter(encounters, appointment);
  const existing = patientId
    ? getClientLocationConfirmationForPatient(encounter, appointment, patientId)
    : getClientLocationConfirmations(encounter, appointment)[0] ?? null;
  return { required: !existing, appointment, existing: patientId ? existing : null };
}

export async function loadPatientForAddressPrefill(
  medplum: MedplumClient,
  patientId: string,
): Promise<Patient | undefined> {
  try {
    return await medplum.readResource('Patient', patientId);
  } catch {
    return undefined;
  }
}
